import React, { useState, useEffect, useRef } from 'react';
import { Grid, Box, Avatar, Typography } from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

const OrderBookApproval = ({ approvalList = [] }) => {
  const [selectedMemo, setSelectedMemo] = useState(null);
  const isAutoSelected = useRef(false);

  useEffect(() => {
    if (!approvalList || approvalList.length === 0) {
      setSelectedMemo(null);
      isAutoSelected.current = false;
      return;
    }

    if (isAutoSelected.current) return;

    const currentStep = approvalList.find(doc => doc && doc.docuYn !== 'Y' && doc.docuYn !== 'N');
    
    if (currentStep?.docuTxt) {
      setSelectedMemo(currentStep);
      isAutoSelected.current = true;
    } else {
      const lastMemo = [...approvalList].reverse().find(doc => doc?.docuYn === 'Y' && doc?.docuTxt);
      if (lastMemo) {
        setSelectedMemo(lastMemo);
        isAutoSelected.current = true;
      }
    }
  }, [approvalList]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일`;
    } catch (e) {
      return dateStr;
    }
  };

  const handleToggleMemo = (doc) => {
    isAutoSelected.current = true;
    if (selectedMemo && selectedMemo.docuStep === doc.docuStep) {
      setSelectedMemo(null);
    } else {
      setSelectedMemo(doc);
    }
  };

  if (!approvalList || approvalList.length === 0) return null;

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container alignItems="center" spacing={0} wrap="nowrap" sx={{ flex: 1, px: 2, overflowX: 'auto', py: 1 }}>
        {approvalList.map((doc, index) => {
          if (!doc) return null;

          const isApproved = doc.docuYn === 'Y';
          const isRejected = doc.docuYn === 'N';
          const statusColor = isApproved ? "#2e7d32" : isRejected ? "#d32f2f" : "#ed6c02";
          const hasMemo = !!doc.docuTxt;
          const isSelected = selectedMemo?.docuStep === doc.docuStep;

          return (
            <React.Fragment key={doc.docuStep || index}>
              <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1.2,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: isSelected ? '#e3f2fd' : 'transparent',
                    cursor: hasMemo ? 'pointer' : 'default',
                    transition: '0.2s',
                    '&:hover': {
                      bgcolor: hasMemo ? '#f0f7ff' : 'transparent'
                    }
                  }}
                  onClick={() => hasMemo && handleToggleMemo(doc)}
                >
                  <Box sx={{ position: 'relative' }}>
                    <Avatar src={doc.docuUsrProfile} sx={{ width: 32, height: 32, border: `2px solid ${statusColor}` }}>
                      {doc.docuUsrNm?.charAt(0)}
                    </Avatar>
                    <Box sx={{
                      position: 'absolute', top: -5, left: -5, bgcolor: '#555', color: '#fff', 
                      borderRadius: '50%', width: 16, height: 16, fontSize: '10px',
                      display: 'flex', justifyContent: 'center', alignItems: 'center', fontWeight: 'bold'
                    }}>
                      {doc.docuStep}
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                      <Typography sx={{ fontWeight: 'bold', fontSize: '0.9rem', color: '#222', whiteSpace: 'nowrap' }}>
                        {doc.docuUsrNm || '미지정'}
                      </Typography>
                      <Typography sx={{ color: statusColor, fontSize: '0.8rem', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                        {isApproved ? '승인' : isRejected ? '반려' : '대기'}
                      </Typography>
                      {hasMemo && (
                        <Box sx={{ 
                          display: 'flex', alignItems: 'center', gap: 0.3, 
                          bgcolor: isSelected ? '#1976d2' : '#f0f4f8', 
                          px: 0.7, py: 0.2, borderRadius: '10px' 
                        }}>
                          <ChatBubbleOutlineIcon sx={{ fontSize: '12px', color: isSelected ? '#fff' : '#1976d2' }} />
                          <Typography sx={{ fontSize: '10px', color: isSelected ? '#fff' : '#1976d2', fontWeight: 'bold' }}>
                            참고
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    {doc.docuDt && (
                      <Typography sx={{ color: '#666', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                        결재일: {formatDate(doc.docuDt)}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {index < approvalList.length - 1 && (
                  <Typography sx={{ mx: 1, color: '#dee2e6', fontWeight: 'bold', userSelect: 'none' }}>❯</Typography>
                )}
              </Grid>
            </React.Fragment>
          );
        })}
      </Grid>

      {selectedMemo && (
        <Box sx={{ 
          mx: 2,
          mt: 1, 
          bgcolor: '#f8fafd', 
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          animation: 'fadeIn 0.25s ease-out' 
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, pt: 1.5, mb: 0.5 }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#1976d2' }}>
               [참고사항 상세 내용] {selectedMemo.docuUsrNm}
            </Typography>
            <Typography 
              sx={{ cursor: 'pointer', p: 0.5, fontSize: '0.8rem', color: '#999', '&:hover': { color: '#333' } }} 
              onClick={() => {
                isAutoSelected.current = true;
                setSelectedMemo(null);
              }}
            >
              닫기 ✕
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: '#444', lineHeight: 1.6, whiteSpace: 'pre-wrap', px: 2, pb: 2 }}>
            {selectedMemo.docuTxt}
          </Typography>
        </Box>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </Box>
  );
};

export default OrderBookApproval;
